import { useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { REVIEWS_MAIN, REVIEWS_SHORT, REVIEWS_DISCLAIMER } from "@/constants/business";

function Stars() {
  return (
    <div className="flex gap-0.5" aria-label="5 étoiles sur 5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 text-copper fill-copper" />
      ))}
    </div>
  );
}

function initials(name) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function ReviewsSection() {
  const scrollRef = useRef(null);
  const [showMore, setShowMore] = useState(false);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section id="avis" data-testid="reviews-section" className="py-14 md:py-20 px-4 md:px-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange">Avis clients</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-navy mt-3 mb-3">
            Ils nous ont fait confiance
          </h2>
          <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
            Découvrez les retours laissés par nos clients après leurs prestations de transport et de déménagement.
          </p>
        </div>
        <div className="hidden sm:flex gap-2 shrink-0">
          <button
            type="button"
            data-testid="reviews-prev"
            onClick={() => scrollBy(-1)}
            aria-label="Avis précédents"
            className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-navy hover:border-navy transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="reviews-next"
            onClick={() => scrollBy(1)}
            aria-label="Avis suivants"
            className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-navy hover:border-navy transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        data-testid="reviews-carousel"
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 md:mx-0 md:px-0"
        style={{ scrollbarWidth: "none" }}
      >
        {REVIEWS_MAIN.map((review, i) => (
          <article
            key={review.name}
            data-testid={`review-card-${i}`}
            className="snap-start shrink-0 w-[86%] sm:w-[47%] lg:w-[31.8%] bg-white border border-gray-200 rounded-2xl p-6 flex flex-col"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-navy text-white text-sm font-bold flex items-center justify-center shrink-0">
                {initials(review.name)}
              </span>
              <div>
                <p className="font-heading font-semibold text-sm text-navy">{review.name}</p>
                <Stars />
              </div>
              <span className="ml-auto text-[10px] font-semibold text-copper bg-copper/10 rounded-full px-2.5 py-1 whitespace-nowrap">
                {review.tag}
              </span>
            </div>
            <p className="text-sm text-navy/80 leading-relaxed flex-1">{review.text}</p>
            <p className="text-[11px] text-muted-foreground mt-4 pt-3 border-t border-gray-100">
              Avis publié sur AlloVoisins — {review.date}
            </p>
          </article>
        ))}
      </div>

      <div className="flex sm:hidden justify-center gap-2 mt-2">
        <button type="button" onClick={() => scrollBy(-1)} aria-label="Avis précédents" className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-navy">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => scrollBy(1)} aria-label="Avis suivants" className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-navy">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-6">
        <button
          type="button"
          data-testid="reviews-show-more"
          onClick={() => setShowMore((s) => !s)}
          className="text-sm font-semibold text-navy border border-gray-300 rounded-full px-5 py-2.5 hover:border-navy transition-colors"
        >
          {showMore ? "Masquer les autres avis" : "Voir plus d'avis"}
        </button>
        {showMore && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4" data-testid="reviews-short-list">
            {REVIEWS_SHORT.map((review) => (
              <div key={review.name} className="bg-graylight border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-semibold text-sm text-navy">{review.name}</p>
                  <Stars />
                </div>
                <p className="text-sm text-navy/75">{review.text}</p>
                <p className="text-[10px] text-muted-foreground mt-2">Avis publié sur AlloVoisins</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-6 max-w-2xl" data-testid="reviews-disclaimer">
        {REVIEWS_DISCLAIMER}
      </p>
    </section>
  );
}
